import React from "react";
import { OrderedSet } from "immutable";
import { connect } from "react-redux";
import debounce from "lodash/debounce";
import { PageContents } from "../../../models";
import { setAutoComplete } from "../../../reducers/autocomplete/actions";
import {
    setDocLastOpened,
    removeDocLastOpened,
} from "../../../reducers/settings/actions";

import { eventEmitter, autoComplete, navigate } from "../../../services";
import { contentHash } from "../../../helpers";
import { areDevToolOpen } from "../../../lib";
import {
    getVisibilityProps,
    EVT_WIN_BEFOREUNLOAD,
    EVT_WIN_RESIZE,
    AUTOCOMPLETE_SELECT,
    AUTOCOMPLETE_REPLACE_VALUE,
    KEY_UP_ARROW,
    KEY_DOWN_ARROW,
    KEY_ENTER,
    KEY_TAB,
    KEY_ESC,
    KEY_P,
    KEY_N,
    CHARS_URDU,
} from "../../../lib";
import { getIds, pageOrDocChanged, compareHashWithContents } from "../helpers";
import {
    getAutoCompleteList,
    updateAutoCompleteList,
    isAutoCompleteCancelKey,
    getWordForAutoComplete,
    replaceAutoCompleteWord,
} from "./helpers";
import {
    hideToolbar,
    onEnterInContentEditable,
} from "../Toolbar/tools/commands";

import Container from "./styles";
import { AxiosResponse } from "axios";
import { AppState } from "../../../store";

export const EVT_EDITOR_CONTENTS_SAVE = "editor/contentsSave";

const { visibilityChange } = getVisibilityProps();
const KEYDOWN_AUTOCOMPLETE_KEYS = [
    KEY_UP_ARROW,
    KEY_DOWN_ARROW,
    KEY_ENTER,
    KEY_TAB,
    KEY_ESC,
];

const mapDispatch = {
    setAutoComplete,
    setDocLastOpened,
    removeDocLastOpened,
};

const mapStateToProps = (state: AppState, { doc_id }: OwnProps) => {
    const { autocomplete, pages } = state;

    //@ts-ignore
    const pagesFiltered = pages.filter(
        (p: any) => (p.get("doc_id") as number) === doc_id
    );
    //@ts-ignore
    const autoCompleteFiltered = autocomplete.filter((_v: any, k: any) => {
        const temp = k.split("-");
        const acDocId = parseInt(temp[0]);
        if (acDocId !== doc_id) {
            return false;
        }

        const acPageId = parseInt(temp[1]);
        return pagesFiltered.has(acPageId);
    });

    return {
        pages: pagesFiltered,
        autocomplete: autoCompleteFiltered,
    };
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatch;

type OwnProps = {
    doc_id: number;
    page_id: number;
    readOnly: boolean;
    enableDarkTheme: boolean;
};

type State = {
    devToolsOpen: boolean;
    counter: number;
};

type Props = StateProps & DispatchProps & OwnProps;

class Contents extends React.Component<Props, State> {
    private contents: string = "";
    private lastSaveHash: string = "";

    private askingAutoComplete: boolean = false;
    private autoCompleteSet = OrderedSet<string>();

    private field: React.RefObject<HTMLDivElement>;
    private save: ReturnType<typeof debounce>;
    private refreshAutoComplete: ReturnType<typeof debounce>;

    constructor(props: Props) {
        super(props);

        this.field = React.createRef<HTMLDivElement>();

        this.state = {
            devToolsOpen: areDevToolOpen(),
            counter: 0,
        };

        this.save = debounce(this._save, 5 * 1000);
        this.refreshAutoComplete = debounce(
            this._refreshAutoComplete,
            5 * 1000
        );
    }

    fetch = async (props: Props) => {
        const { doc_id, page_id } = getIds(props);
        try {
            const { data: contents }: AxiosResponse<string> =
                await PageContents.fetch(page_id, {
                    doc_id,
                });
            this.contents = contents;
            const list = getAutoCompleteList(contents);
            this.props.setAutoComplete({ doc_id, page_id, list });
            this.props.setDocLastOpened({ doc_id, page_id });
            this.setState({ counter: this.state.counter + 1 });
        } catch (e) {
            console.log("e");
            console.log(e);
            //@ts-ignore
            if (e.request.status === 404) {
                this.props.removeDocLastOpened({ doc_id });
                navigate.to("/app/docs");
            }
        }
    };

    _refreshAutoComplete = (contents: string) => {
        const list = getAutoCompleteList(contents);
        const { doc_id, page_id } = getIds(this.props);
        this.props.setAutoComplete({ doc_id, page_id, list });
    };

    componentDidMount() {
        this.focus();
        this.fetch(this.props);

        window.addEventListener(visibilityChange, this.onVisibilityChange);
        window.addEventListener(EVT_WIN_BEFOREUNLOAD, this.onWinBeforeUnload);
        window.addEventListener(EVT_WIN_RESIZE, this.onWinResize);
        eventEmitter.on(EVT_EDITOR_CONTENTS_SAVE, this.onEitorContentsSave);
        eventEmitter.on(
            AUTOCOMPLETE_REPLACE_VALUE,
            this.replaceValueFromAutoComplete
        );
    }

    componentWillUnmount() {
        this.save.flush();
        autoComplete.clear();
        this.refreshAutoComplete.flush();

        window.removeEventListener(visibilityChange, this.onVisibilityChange);
        window.removeEventListener(
            EVT_WIN_BEFOREUNLOAD,
            this.onWinBeforeUnload
        );
        window.removeEventListener(EVT_WIN_RESIZE, this.onWinResize);
        eventEmitter.off(EVT_EDITOR_CONTENTS_SAVE, this.onEitorContentsSave);
        eventEmitter.off(
            AUTOCOMPLETE_REPLACE_VALUE,
            this.replaceValueFromAutoComplete
        );
    }

    componentWillReceiveProps(nextProps: Props, nextState: State) {
        if (pageOrDocChanged(this.props, nextProps)) {
            this.save.flush();
            hideToolbar();
            this.fetch(nextProps);
        }

        nextProps.autocomplete.valueSeq().forEach((v: any) => {
            this.autoCompleteSet = this.autoCompleteSet.merge(v);
        });
        updateAutoCompleteList(this.autoCompleteSet);
    }

    shouldComponentUpdate(nextProps: Props, nextState: State) {
        return (
            this.state.counter !== nextState.counter ||
            this.state.devToolsOpen !== nextState.devToolsOpen ||
            this.props.enableDarkTheme !== nextProps.enableDarkTheme
        );
    }

    _save = async () => {
        const contents = this.contents;

        // prevent, accident errors sending blank state to server
        // until server level versioning is enabled this check must be here
        if (!contents) {
            return;
        }

        if (!compareHashWithContents(this.lastSaveHash, contents)) {
            this.lastSaveHash = contentHash(contents);

            try {
                const { doc_id, page_id } = getIds(this.props);
                await PageContents.save({ contents }, page_id, { doc_id });
            } catch (e) {
                console.log(e);
            }
        }
    };

    onEitorContentsSave = () => {
        if (this.field && this.field.current) {
            this.onChange(this.field.current.innerHTML);
        }
    };

    onWinBeforeUnload = () => this.save.flush();

    onWinResize = () => this.setState({ devToolsOpen: areDevToolOpen() });

    onVisibilityChange = () => {
        if (document.hidden) {
            this.save.flush();
        }
    };

    onChange = (contents: string) => {
        this.contents = contents;
        this.save();
        this.refreshAutoComplete(contents);
    };

    onKeyDown = (evt: React.KeyboardEvent<HTMLDivElement>) => {
        const key = evt.key.toUpperCase();

        if (autoComplete.isVisible()) {
            if (evt.ctrlKey && [KEY_P, KEY_N].includes(key)) {
                if (key === KEY_P) {
                    autoComplete.prev();
                } else if (key === KEY_N) {
                    autoComplete.next();
                }

                evt.preventDefault();
                return false;
            }

            if (KEYDOWN_AUTOCOMPLETE_KEYS.includes(key)) {
                if (key === KEY_UP_ARROW) {
                    autoComplete.prev();
                } else if (key === KEY_DOWN_ARROW) {
                    autoComplete.next();
                } else if (key === KEY_TAB || key === KEY_ENTER) {
                    eventEmitter.emit(AUTOCOMPLETE_SELECT);
                    autoComplete.hide();
                } else if (key === KEY_ESC) {
                    autoComplete.hide();
                }

                evt.preventDefault();
                return false;
            }

            if (isAutoCompleteCancelKey(key)) {
                autoComplete.hide();
            }
        }
    };

    clearAskingAutoComplete = () => (this.askingAutoComplete = false);
    onKeyUp = (evt: React.KeyboardEvent<HTMLDivElement>) => {
        const key = evt.key;

        if (!this.askingAutoComplete && CHARS_URDU.includes(key)) {
            const word = getWordForAutoComplete();
            if (word && word.length > 0) {
                this.askingAutoComplete = true;
                autoComplete.ask(word);

                setTimeout(this.clearAskingAutoComplete, 250);
            }
        }

        if (key === KEY_ENTER) {
            onEnterInContentEditable();
        }

        if (evt.target) {
            this.onChange((evt.target as HTMLDivElement).innerHTML);
        }
    };

    replaceValueFromAutoComplete = (fromWord: string, toWord: string) =>
        replaceAutoCompleteWord(fromWord, toWord);

    onClick = () => {
        autoComplete.hide();
    };

    markup() {
        return {
            __html: this.contents,
        };
    }

    focus = () =>
        this.field && this.field.current && this.field.current.focus();

    render() {
        // const { devToolsOpen } = this.state
        const { readOnly, enableDarkTheme } = this.props;
        return (
            <Container enableDarkTheme={enableDarkTheme}>
                <div
                    ref={this.field}
                    spellCheck={false}
                    // contentEditable={!readOnly && !devToolsOpen}
                    contentEditable={!readOnly}
                    onKeyDown={this.onKeyDown}
                    onKeyUp={this.onKeyUp}
                    onClick={this.onClick}
                    dangerouslySetInnerHTML={this.markup()}
                />
            </Container>
        );
    }
}

export default connect(mapStateToProps, mapDispatch)(Contents);
